/**
 * Вложение в сообщении.
 *
 * Содержимое запрашивается по чату и сообщению, а не по идентификатору файла:
 * fileId клиенту не отдаётся вовсе, и решает, какой файл относится к
 * сообщению, сервер. Иначе появился бы способ вытянуть произвольный файл
 * сессии в обход проверки доступа к чату.
 *
 * Подпись сюда не входит: она приходит в поле text сообщения и рисуется
 * обычным текстом рядом. Для получателя подпись — часть сообщения, а не
 * свойство файла.
 */

import { useState } from 'react';
import type { TelegramMediaDto } from '@tw/shared-types';
import { DownloadIcon, FileIcon, ImageIcon, MicIcon, MusicIcon, VideoIcon } from './icons.tsx';
import { cn } from '@/lib/utils';
import { MediaViewer } from './MediaViewer.tsx';

interface MessageMediaProps {
  media: TelegramMediaDto;
  chatId: string;
  messageId: string;
  outgoing: boolean;
}

const LABELS: Record<TelegramMediaDto['kind'], string> = {
  photo: 'Фотография',
  video: 'Видео',
  animation: 'GIF',
  document: 'Файл',
  voice: 'Голосовое сообщение',
  audio: 'Аудио',
  videoNote: 'Видеосообщение',
  sticker: 'Стикер',
  other: 'Вложение',
};

const ICONS: Record<TelegramMediaDto['kind'], typeof FileIcon> = {
  photo: ImageIcon,
  video: VideoIcon,
  animation: VideoIcon,
  document: FileIcon,
  voice: MicIcon,
  audio: MusicIcon,
  videoNote: VideoIcon,
  sticker: ImageIcon,
  other: FileIcon,
};

export function MessageMedia({ media, chatId, messageId, outgoing }: MessageMediaProps) {
  const [failed, setFailed] = useState(false);

  /*
   * Открыт ли полноэкранный просмотр.
   *
   * Состояние держится здесь, а не в ленте: вложение знает, что оно за файл,
   * и открывать просмотр стикера или голосового не нужно.
   */
  const [viewing, setViewing] = useState(false);

  const src = `/api/chats/${chatId}/messages/${encodeURIComponent(messageId)}/media`;

  const visual = media.kind === 'photo' || media.kind === 'sticker' || media.kind === 'animation';
  const playable = media.kind === 'video' || media.kind === 'videoNote';
  const listenable = media.kind === 'voice' || media.kind === 'audio';

  // Отказ загрузки возвращает нас к описанию: пустой прямоугольник на месте
  // фотографии выглядит как поломка интерфейса, а не как недоступный файл.
  if (!failed) {
    if (visual) {
      /*
       * Стикер не открывается: увеличивать его не для чего, а клик по нему
       * читался бы как «поставить такой же».
       */
      const zoomable = media.kind !== 'sticker';

      const image = (
        <img
          src={src}
          alt={media.kind === 'sticker' ? (media.emoji ?? 'Стикер') : 'Вложение'}
          loading="lazy"
          onError={() => setFailed(true)}
          // Соотношение сторон известно заранее — резервируем место, чтобы
          // лента не прыгала при подгрузке.
          style={
            media.width && media.height
              ? { aspectRatio: `${media.width} / ${media.height}` }
              : undefined
          }
          className={cn(
            'mb-1 block h-auto w-full rounded-xl object-cover',
            media.kind === 'sticker' ? 'max-w-[180px] bg-transparent' : 'max-w-[320px] bg-black/20',
            zoomable && 'cursor-zoom-in',
          )}
        />
      );

      if (!zoomable) return image;

      return (
        <>
          <button
            type="button"
            aria-label="Открыть вложение"
            onClick={() => setViewing(true)}
            className="block border-transparent bg-transparent p-0 text-left"
          >
            {image}
          </button>
          {viewing && (
            <MediaViewer media={media} src={src} onClose={() => setViewing(false)} />
          )}
        </>
      );
    }

    if (playable) {
      /*
       * Видеосообщение показывается кругом, а не прямоугольником.
       *
       * Это не украшательство: кружок и обычное видео — разные типы сообщений
       * с разными ожиданиями. Прямоугольный кружок читается как присланный
       * файл, и менеджер отвечает на него не так, как ответил бы на
       * видеосообщение. Квадрат гарантирован форматом: непрямоугольным
       * видеосообщение не бывает.
       */
      if (media.kind === 'videoNote') {
        return (
          <video
            src={src}
            controls
            preload="metadata"
            onError={() => setFailed(true)}
            className="mb-1 block size-[220px] rounded-full bg-black/30 object-cover"
          />
        );
      }

      return (
        <div className="relative mb-1 w-fit">
          <video
            src={src}
            controls
            preload="metadata"
            onError={() => setFailed(true)}
            className="block max-w-[320px] rounded-xl bg-black/30"
          />
          {/* Отдельная кнопка, а не клик по видео: клик по видео — это пауза,
              и отнимать её ради увеличения нельзя. */}
          <button
            type="button"
            aria-label="Открыть во весь экран"
            title="Открыть во весь экран"
            onClick={() => setViewing(true)}
            className="absolute right-1.5 top-1.5 grid size-7 place-items-center rounded-full border-transparent bg-black/50 text-white transition-colors hover:bg-black/70"
          >
            <ImageIcon className="size-4" />
          </button>
          {viewing && (
            <MediaViewer media={media} src={src} onClose={() => setViewing(false)} />
          )}
        </div>
      );
    }

    if (listenable) {
      return (
        <div className="mb-1 flex min-w-[240px] flex-col gap-1">
          <audio
            src={src}
            controls
            preload="metadata"
            onError={() => setFailed(true)}
            className="w-full"
          />
          {media.fileName && (
            <span className="truncate text-[11px] opacity-70">{media.fileName}</span>
          )}
        </div>
      );
    }
  }

  /*
   * Опрос, геопозиция, контакт — файла за ними нет, и ссылка «скачать» вела
   * бы в никуда. Показываем подписью, что это, без кнопки загрузки.
   */
  if (media.kind === 'other') {
    return (
      <div
        className={cn(
          'mb-1 flex items-center gap-2.5 rounded-xl px-2.5 py-2',
          outgoing ? 'bg-black/15' : 'bg-black/20 dark:bg-white/5',
        )}
      >
        <span
          className={cn(
            'grid size-9 flex-none place-items-center rounded-lg',
            outgoing ? 'bg-white/20' : 'bg-white/10',
          )}
        >
          <FileIcon className="size-[18px]" />
        </span>
        <span className="min-w-0 truncate text-sm font-medium">{media.label ?? LABELS.other}</span>
      </div>
    );
  }

  return <MediaCard media={media} src={src} outgoing={outgoing} unavailable={failed} />;
}

/** Карточка для файлов, которые не показать прямо в ленте, и для сбоев загрузки. */
function MediaCard({
  media,
  src,
  outgoing,
  unavailable,
}: {
  media: TelegramMediaDto;
  src: string;
  outgoing: boolean;
  unavailable: boolean;
}) {
  const Icon = ICONS[media.kind];

  const details = [
    media.fileName ? LABELS[media.kind] : null,
    formatDuration(media.duration),
    formatSize(media.size),
    media.width && media.height ? `${media.width}×${media.height}` : null,
  ].filter(Boolean);

  return (
    <a
      href={src}
      download={media.fileName ?? undefined}
      className={cn(
        'mb-1 flex items-center gap-2.5 rounded-xl px-2.5 py-2 transition-colors',
        outgoing ? 'bg-black/15 hover:bg-black/25' : 'bg-black/20 hover:bg-black/30 dark:bg-white/5 dark:hover:bg-white/10',
      )}
    >
      <span
        className={cn(
          'grid size-9 flex-none place-items-center rounded-lg',
          outgoing ? 'bg-white/20' : 'bg-white/10',
        )}
      >
        <Icon className="size-[18px]" />
      </span>

      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-sm font-medium">
          {media.fileName ?? LABELS[media.kind]}
        </span>
        <span className="truncate text-[11px] opacity-70">
          {unavailable ? 'Не удалось загрузить' : details.join(' · ')}
        </span>
      </span>

      <DownloadIcon className="ml-auto size-4 flex-none opacity-60" />
    </a>
  );
}

/** Байты в человеческий вид. Килобайт двоичный — как в файловых менеджерах. */
function formatSize(bytes: number | null): string | null {
  if (!bytes || bytes <= 0) return null;

  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }

  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

function formatDuration(seconds: number | null): string | null {
  if (!seconds || seconds <= 0) return null;

  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
